import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import moment from "moment-timezone";

import { useAuth } from "../hooks/useAuth";
import useEvents from "../hooks/useEvents";
import usePageTitle from "../hooks/usePageTitle";

import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Skeleton from "@mui/material/Skeleton";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";

import Modal from "../Components/Modal";

import "../App.css";

function EventList() {
  const { authed } = useAuth();
  const { events, error, loading } = useEvents();
  const [isNonAuthedModalOpen, toggleIsNonAuthedModalOpen] = useState(false);

  const navigate = useNavigate();
  // Only show events that are at least 3 hours ahead
  const inThreeHours = moment().add(3, "h");

  const activeEvents = events.filter((event) => {
    return moment(event.date).isAfter(inThreeHours);
  });

  const pastEvents = events
    .filter((event) => moment(event.date).isBefore(inThreeHours))
    .sort((a, b) => moment(b.date).diff(moment(a.date)));

  const handleLoginPressed = () => {
    navigate("/login");
  };

  const handleJoinEventClicked = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const handleCreateEventClicked = (locationId) => {
    if (authed) {
      navigate(`/events/create`);
    } else {
      toggleIsNonAuthedModalOpen(true);
    }
  };

  usePageTitle(`Events | Fudopia | Meet. Eat. Repeat.`);

  function NonAuthedModal() {
    return (
      <Modal
        title="Be one of the first foodies in Bangkok!"
        isOpen={isNonAuthedModalOpen}
        handleDismiss={() => toggleIsNonAuthedModalOpen(!isNonAuthedModalOpen)}
      >
        <p style={{ textAlign: "center" }}>
          We’re offering free event access for the first 200 signups. No
          subscription needed for a whole year.
        </p>
        <p style={{ marginBottom: 40, textAlign: "center" }}>
          Create or join foodie meetups and help us kick things off in the
          tastiest way.
        </p>
        <div className="btn-inline">
          <Button
            color="primary"
            variant="contained"
            onClick={() => toggleIsNonAuthedModalOpen(!isNonAuthedModalOpen)}
          >
            Not now
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleLoginPressed}
          >
            Login/Register
          </Button>
        </div>
      </Modal>
    );
  }
  return (
    <>
      <NonAuthedModal />
      {error && <p className="error-message">Something went wrong...</p>}
      <Typography variant="h1" component="div" sx={{ flexGrow: 0 }}>
        Events
      </Typography>
      <div className="block-wrapper">
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={() => handleCreateEventClicked()}
        >
          Create your own event!
        </Button>
      </div>
      <div className="block-wrapper events grid">
        {loading && (
          <Grid container spacing={4} sx={{ justifyContent: "space-between" }}>
            {[...Array(10)].map((x, i) => (
              <Grid key={i}>
                <Skeleton
                  animation="wave"
                  height={500}
                  width="21vw"
                  sx={{ marginRight: 5 }}
                />
              </Grid>
            ))}
          </Grid>
        )}
        {!loading && !activeEvents.length && (
          <div className="notice">
            <h2 className="centered">
              There are no events at the moment. <br />
              But you can create one!
            </h2>
            <p>Choose a dish to try at your next event</p>
            <Link to="/dishes">
              <Button color="primary" variant="contained" size="small">
                See Dishes
              </Button>
            </Link>
          </div>
        )}
        {activeEvents?.map((event, key) => {
          const currentCount = event.clients?.length ?? event.client_count ?? 0; // TEMP: fallback until backend sends a consistent field for authed + non-authed
          const spotsLeft = 4 - currentCount;

          return (
            <div key={key} className="block" style={{ position: "relative" }}>
              <span
                className={`spots-badge ${spotsLeft <= 0 ? "spots-badge--full" : ""}`}
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  padding: "4px 10px",
                  borderRadius: "999px",
                  background: spotsLeft <= 0 ? "#D3D1C7" : "#EAF3DE",
                  color: spotsLeft <= 0 ? "#2C2C2A" : "#173404",
                  zIndex: 1,
                }}
              >
                {spotsLeft <= 0
                  ? "Event full"
                  : spotsLeft === 1
                    ? "1 spot left"
                    : `${spotsLeft} spots left`}
              </span>
              <h2
                onClick={() => handleJoinEventClicked(event.id)}
                style={{ cursor: "pointer" }}
              >
                {event.title}
              </h2>
              {authed && (
                <>
                  <h3 className="subheader">Where You'll Meet</h3>
                  <span>{event.location?.title}</span>
                </>
              )}
              <h3 className="subheader">Date & Time</h3>
              <span>
                {moment(event.date)
                  .tz(event.timezone)
                  .format(authed ? "dddd, MMMM Do · h:mm a" : "dddd, MMMM Do")}
              </span>
              {event.tags.length > 0 && (
                <>
                  <h3 className="subheader">Meetup Mood</h3>
                  <ul className="tags-list">
                    {event.tags.map((tag) => (
                      <li key={tag.id}>{tag.title}</li>
                    ))}
                  </ul>
                </>
              )}
              <h3 className="subheader">What are we trying?</h3>
              {event.dish?.image_url ? (
                <img
                  src={event.dish.image_url}
                  alt={event.dish.title}
                  className="event-dish"
                />
              ) : (
                <div
                  className="event-dish dish-card-img--placeholder centered"
                  role="img"
                  aria-label="Custom dish"
                >
                  🍽️
                </div>
              )}
              {spotsLeft <= 0 ? (
                <p>This event is full</p>
              ) : (
                <div className="sm-button-wrapper">
                  <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    className="event-list-button"
                    onClick={() => handleJoinEventClicked(event.id)}
                  >
                    Join this event
                  </Button>
                </div>
              )}
            </div>
          );
        })}
        {pastEvents.length > 0 && (
          <Container sx={{ marginTop: "60px", marginBottom: "60px" }}>
            <Typography
              variant="h2"
              component="div"
              sx={{ flexGrow: 0, mb: 3, fontWeight: "800" }}
            >
              Past Events
            </Typography>
          </Container>
        )}
        {pastEvents?.map((event, key) => (
          <div key={key} className="block past-events">
            <Link to={`/events/${event.id}`}>
              <h2>{event.title}</h2>
            </Link>
            <h3 className="subheader">Restaurant</h3>
            <span>{event.location?.title}</span>
            <h3 className="subheader">Date & Time</h3>
            <span>
              {moment(event.date)
                .tz(event.timezone)
                .format(authed ? "dddd, MMMM Do · h:mm a" : "dddd, MMMM Do")}
            </span>
            {event.tags.length > 0 && (
              <>
                <h3 className="subheader">Meetup Mood</h3>
                <ul className="tags-list">
                  {event.tags.map((tag) => (
                    <li key={tag.id}>{tag.title}</li>
                  ))}
                </ul>
              </>
            )}
            <h3 className="subheader">What did we try?</h3>
            {event.dish?.image_url ? (
              <img
                src={event.dish.image_url}
                alt={event.dish.title}
                className="event-dish"
              />
            ) : (
              <div
                className="event-dish dish-card-img--placeholder centered"
                role="img"
                aria-label="Custom dish"
              >
                🍽️
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

export default EventList;
